<template>
    <div>
        <div v-if="isLoading" class="l-stack l-center l-padded">
            <VSpinner size="medium" line-fg-color="#000" :speed="1" />
        </div>

        <ApexChart
            v-else
            :options="chartOptionsComputed"
            :series="series"
            :height="chartHeight"
        />
    </div>
</template>

<script>
import moment from 'moment'
import ApexChart from 'vue-apexcharts'
import VSpinner from 'vue-simple-spinner'
import ChartHelper from '@/mixins/ChartHelper'
import UrlHelper from '@/mixins/UrlHelper'

import { httpHelper, measurementHelper } from '@/utils'
import { debounce } from 'lodash'

const measurementOptions = [...measurementHelper.measurements]

export default {
    name: 'MeasurementsChart',
    components: {
        VSpinner,
        ApexChart,
    },
    mixins: [ChartHelper, UrlHelper],
    props: {
        asset: {
            type: Object,
            required: true,
        },
        date: {
            type: String,
            default: () => moment().format('YYYY-MM-DD'),
        },
        chartHeight: {
            type: String,
            default: '400px',
        },
        measurements: {
            type: [Array, String],
            required: true,
            validator: value => {
                return Array.isArray(value)
                    ? value.every(measurement =>
                          measurementOptions.includes(measurement)
                      )
                    : measurementOptions.includes(value)
            },
        },
    },
    data() {
        return {
            chartDefaults: {
                chart: {
                    stacked: false,
                    animations: {
                        enabled: false,
                    },
                    zoom: {
                        type: 'x',
                        enabled: true,
                        autoScaleYaxis: true,
                    },
                },
                dataLabels: {
                    enabled: false,
                },
                markers: {
                    size: 0,
                    style: 'full',
                },
                xaxis: {
                    type: 'datetime',
                },
                tooltip: {
                    shared: false,
                    x: {
                        format: 'dd.MM.yy HH:mm:ss',
                    },
                },
                stroke: {
                    curve: this.curveTypes,
                    width: 3,
                },
            },
            isLoading: true,
            series: [],
            responseData: [],
        }
    },
    computed: {
        chartOptionsComputed() {
            return {
                ...this.chartDefaults,
                xaxis: {
                    type: 'datetime',
                    min: moment(this.date)
                        .startOf('day')
                        .valueOf(),
                    max: moment(this.date)
                        .endOf('day')
                        .valueOf(),
                },
                yaxis: this.measurements.map((measurement, index) => ({
                    seriesName: measurement,
                    min: this.getYMinValue(measurement),
                    max: this.getYMaxValue(measurement),
                    decimalsInFloat: 2,
                    title: {
                        text: this.getMeasurementLabel(measurement),
                    },
                    opposite: index % 2 === 0,
                })),
            }
        },
    },
    mounted() {
        moment.locale(this.$i18n.locale)
        this.load()
    },
    created() {
        moment.locale(this.$i18n.locale)
    },
    methods: {
        load: debounce(async function() {
            this.isLoading = true
            await this.fetchMeasurements()
            this.renderData()
            this.isLoading = false
        }, 500),
        async fetchMeasurements() {
            try {
                if (!this.asset || !this.asset.asset_details) {
                    console.error('Invalid assets', this.asset)
                    return
                }
                if (!this.date) {
                    console.error('Invalid dates', this.date)
                    return
                }
                const startDate = moment(this.date)
                    .startOf('day')
                    .toISOString()
                const endDate = moment(this.date)
                    .endOf('day')
                    .toISOString()

                let url =
                    'measurements/?' +
                    `tracker=${this.asset.id}` +
                    '&fields=timestamp,sensor_data' +
                    `&timestamp_min=${startDate}` +
                    `&timestamp_max=${endDate}` +
                    `&limit=${process.env.VUE_APP_LIMIT_RECORDS_PER_REQUEST}`

                while (url) {
                    const { data } = await httpHelper.get(url)
                    this.responseData = this.responseData.concat(data.results)
                    url = data.next
                }
            } catch (error) {
                console.error('Error fetching measurements:', error)
            }
        },
        renderData() {
            this.series = []
            if (!this.responseData || this.responseData.length === 0) {
                return
            }
            this.measurements.map(measurement => {
                const label = this.getMeasurementLabel(measurement)
                const converter =
                    measurementHelper.converters[measurement] ||
                    measurementHelper.converters.default
                const data = this.responseData.filter(
                    item => item?.sensor_data?.[measurement]?.value
                )
                this.series.push({
                    key: measurement,
                    name: label,
                    data: data.map(item => [
                        item.timestamp,
                        converter(item.sensor_data[measurement].value),
                    ]),
                })
            })
        },
        getMeasurementLabel(measurement) {
            return this.$root.$te(`shared.measurements.${measurement}`)
                ? this.$t(`shared.measurements.${measurement}`)
                : measurement
        },
    },
}
</script>
